// src/components/Auth/ForgotPassword.js
import React, { useState, useEffect } from 'react';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../../firebase/config';
import { Link } from 'react-router-dom';
import './Auth.css';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Aplicar dark theme quando o componente montar
  useEffect(() => {
    // Aplicar dark theme ao root para garantir que o tema se aplique a tudo
    document.documentElement.classList.add('dark-theme');
    
    // Cleanup function para remover a classe quando o componente for desmontado
    return () => {
      document.documentElement.classList.remove('dark-theme');
    };
  }, []);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setError('Por favor, informe seu email.');
      return;
    }
    
    try {
      setError('');
      setLoading(true);
      
      // Chamar a função de redefinição de senha do Firebase
      await sendPasswordResetEmail(auth, email);
      
      // Exibir mensagem de sucesso
      setSuccess(true);
    } catch (error) {
      console.error('Erro ao enviar email de redefinição:', error);
      
      // Tratar erros específicos
      if (error.code === 'auth/user-not-found') {
        setError('Não encontramos uma conta com este email. Verifique o email informado.');
      } else if (error.code === 'auth/invalid-email') {
        setError('Email inválido. Por favor, verifique o formato do email.');
      } else {
        setError('Erro ao enviar email de redefinição. Por favor, tente novamente mais tarde.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container dark-theme">
      <div className="auth-card">
        <div className="auth-logo">
          <div className="logo-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5-5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"></path>
            </svg>
          </div>
          <h2>Recuperar Senha</h2>
        </div>
        
        {error && <div className="error-message animate-fade-in">{error}</div>}
        
        {success ? (
          <div className="success-message animate-fade-in">
            <div className="success-icon-container">
              <svg xmlns="http://www.w3.org/2000/svg" className="success-check-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
            </div>
            <h3>Email Enviado!</h3>
            <p>Enviamos um email com instruções para redefinir sua senha.</p>
            <p>Por favor, verifique sua caixa de entrada e siga as instruções no email.</p>
            <div className="auth-link return-link">
              <Link to="/login" className="login-button">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 14L4 9l5-5"></path>
                  <path d="M20 20v-7a4 4 0 0 0-4-4H4"></path>
                </svg>
                <span>Voltar para o login</span>
              </Link>
            </div>
          </div>
        ) : (
          <>
            <p className="auth-instructions">Informe seu email para receber um link de redefinição de senha.</p>
            
            <form onSubmit={handleResetPassword} className="auth-form">
              <div className="form-group">
                <label htmlFor="email">
                  <svg xmlns="http://www.w3.org/2000/svg" className="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                    <polyline points="22,6 12,13 2,6"></polyline>
                  </svg>
                  Email
                </label>
                <div className="input-container">
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Seu email"
                    disabled={loading}
                    className="input-with-icon"
                  />
                </div>
              </div>
              
              <button type="submit" className="login-button" disabled={loading}>
                {loading ? (
                  <>
                    <span className="spinner"></span>
                    <span>Enviando...</span>
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                      <polyline points="22,6 12,13 2,6"></polyline>
                    </svg>
                    <span>Enviar link de redefinição</span>
                  </>
                )}
              </button>
            </form>
            
            <div className="auth-links">
              <p className="auth-link">
                <Link to="/login">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="19" y1="12" x2="5" y2="12"></line>
                    <polyline points="12 19 5 12 12 5"></polyline>
                  </svg>
                  Voltar para o login
                </Link>
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;